/**
 * Проверка патентов в расчёте (WS 15587410, п.4): вывод человека «чисто / не чисто» и
 * снимок того, что сказал IP Shield, когда вывод делали. Логика отдельно от виджета.
 */
import type { PatentFindings, PatentStatus } from '../api/client';

export type PatentVerdict = 'clean' | 'not_clean';
export type RiskTone = 'error' | 'warning' | 'success' | 'default';

/** То, что сохраняется в `calculation_data.patent_check`. */
export interface PatentCheckSnapshot {
  verdict: PatentVerdict | null;
  risk_level?: string | null;
  risk_score?: number | null;
  risk_source?: 'manual' | 'auto' | null;
  checked_at?: string | null;
  findings?: PatentFindings | null;
}

const FINDING_KEYS: (keyof PatentFindings)[] = ['design_patents', 'utility_patents', 'trademarks', 'ip_findings'];

/** Цвет уровня риска IP Shield (critical/high/medium/low; ручной `clean`). */
export function riskTone(level: string | null | undefined): RiskTone {
  switch ((level ?? '').toLowerCase()) {
    case 'critical':
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
    case 'clean':
      return 'success';
    default:
      return 'default';
  }
}

/** Снимок статуса IP Shield с выводом человека — для сохранения в расчёте. */
export function snapshotFrom(status: PatentStatus | null, verdict: PatentVerdict | null, prev: PatentCheckSnapshot | null): PatentCheckSnapshot {
  if (!status?.checked) return { ...(prev ?? {}), verdict };
  return {
    verdict,
    risk_level: status.risk_level ?? null,
    risk_score: status.risk_score ?? null,
    risk_source: status.risk_source ?? null,
    checked_at: status.checked_at ?? null,
    findings: status.findings ?? null,
  };
}

/** Есть ли в снимке что сохранять: пустой снимок в расчёт не пишем. */
export function isEmptySnapshot(s: PatentCheckSnapshot | null): boolean {
  return !s || (!s.verdict && !s.risk_level && !s.checked_at);
}

function str(v: unknown, max: number): string | null {
  return typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : null;
}

/** Снимок из сохранённого расчёта: каждое поле проверяется, чужое отбрасывается. */
export function parsePatentCheck(raw: unknown): PatentCheckSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const findings =
    r.findings && typeof r.findings === 'object'
      ? (Object.fromEntries(
          FINDING_KEYS.map((k) => {
            const v = (r.findings as Record<string, unknown>)[k];
            return [k, typeof v === 'number' && Number.isFinite(v) ? v : 0];
          }),
        ) as unknown as PatentFindings)
      : null;
  const snapshot: PatentCheckSnapshot = {
    verdict: r.verdict === 'clean' || r.verdict === 'not_clean' ? r.verdict : null,
    risk_level: str(r.risk_level, 16),
    risk_score: typeof r.risk_score === 'number' && Number.isFinite(r.risk_score) ? r.risk_score : null,
    risk_source: r.risk_source === 'manual' || r.risk_source === 'auto' ? r.risk_source : null,
    checked_at: str(r.checked_at, 40),
    findings,
  };
  return isEmptySnapshot(snapshot) ? null : snapshot;
}

/** Дата проверки для подписи: «30.09.2026». Мусор — null. */
export function formatCheckedAt(value: string | null | undefined, locale: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString(locale);
}
